import { useEffect, useRef, useState } from "react";

/**
 * A button that opens a popover. Closes on outside click, Escape, or when a child calls `close`.
 * `button` is the trigger's content; the trigger itself is styled by `triggerClass`.
 */
export default function Menu({ label, button, triggerClass = "tb-btn", align = "left", className = "", children }: {
  label: string;
  button: React.ReactNode;
  triggerClass?: string;
  align?: "left" | "right";
  className?: string;
  children: (close: () => void) => React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const down = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("mousedown", down);
    window.addEventListener("keydown", key);
    return () => { window.removeEventListener("mousedown", down); window.removeEventListener("keydown", key); };
  }, [open]);

  return (
    <div className={`menu-wrap ${className}`} ref={ref}>
      <button type="button" className={triggerClass} aria-haspopup="menu" aria-expanded={open} aria-label={label} title={label} onClick={() => setOpen((o) => !o)}>
        {button}
      </button>
      {open && <div className={`popover ${align}`} role="menu">{children(() => setOpen(false))}</div>}
    </div>
  );
}
